import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router-dom"

interface PageTransitionProps {
  children: React.ReactNode
}

export function PageTransition({ children }: PageTransitionProps) {
  const location = useLocation()
  const navigationType = useNavigationType()
  const [displayChildren, setDisplayChildren] = useState(children)
  const [transitionStage, setTransitionStage] = useState<"enter" | "exit" | "idle">("idle")
  const prevPathRef = useRef(location.pathname)

  useEffect(() => {
    // If path changed
    if (location.pathname !== prevPathRef.current) {
      // Start exit animation
      setTransitionStage("exit")

      // After exit animation, update children and start enter
      const exitTimer = setTimeout(() => {
        setDisplayChildren(children)
        setTransitionStage("enter")

        // After enter animation, go to idle
        const enterTimer = setTimeout(() => {
          setTransitionStage("idle")
        }, 200)

        return () => clearTimeout(enterTimer)
      }, 150)

      prevPathRef.current = location.pathname

      return () => clearTimeout(exitTimer)
    } else {
      // Same path, just update children
      setDisplayChildren(children)
    }
  }, [children, location.pathname])

  // Determine animation direction based on navigation type
  const isBack = navigationType === "POP"

  const getTransitionClasses = () => {
    if (transitionStage === "idle") {
      return "opacity-100 translate-x-0"
    }

    if (transitionStage === "exit") {
      // Exit animation
      return isBack
        ? "opacity-0 translate-x-4" // Going back: exit to right
        : "opacity-0 -translate-x-4" // Going forward: exit to left
    }

    if (transitionStage === "enter") {
      // Enter animation - start from opposite side
      return isBack
        ? "opacity-100 translate-x-0 animate-slide-in-left" // Coming back: slide from left
        : "opacity-100 translate-x-0 animate-slide-in-right" // Coming forward: slide from right
    }

    return ""
  }

  return (
    <div className="overflow-x-clip">
      <div
        className={`transition-all duration-200 ease-out ${getTransitionClasses()}`}
        style={{ minHeight: "100vh" }}
      >
        {displayChildren}
      </div>
    </div>
  )
}
